import React from 'react';
import { motion } from 'framer-motion';
import ListCheck from '@/components/ui/list/components/List.Check';
import { FADE_UP } from '@/theme/motion';
import type { TypeListItemState, TypeListLayout } from '@/types/list/types.list';
import type { TypeSelectionSide } from '@/types/list/types.list.selection';

type Props<T> = {
    item: T;
    itemKey: string;
    index: number;
    posinset: number;
    setsize: number;
    layout: TypeListLayout['type'];
    render: (item: T, state: TypeListItemState) => React.ReactNode;
    label?: string;
    tabIndex: 0 | -1;
    active: boolean;
    current: boolean;
    checked: boolean | null;
    checkLabel?: string;
    checkAt: TypeSelectionSide;
    animated: boolean;
    onActivate?: (index: number) => void;
    onToggle?: (index: number, extend: boolean) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>, index: number) => void;
    onFocusKey: (key: string) => void;
    measureKey?: string;
    measure?: (key: string, node: HTMLElement | null) => void;
};

const INTERACTIVE = 'a, button, input, select, textarea, label, [role="button"], [role="menuitem"], [data-list-ignore]';

function ListCell<T>({
    item, itemKey, index, posinset, setsize, layout, render, label, tabIndex, active, current, checked, checkLabel, checkAt,
    animated, onActivate, onToggle, onKeyDown, onFocusKey, measureKey, measure,
}: Props<T>) {
    const onClick = onActivate
        ? (event: React.MouseEvent<HTMLElement>) => {
            const target = event.target as HTMLElement;
            if (!event.currentTarget.contains(target)) return;
            const inner = target.closest(INTERACTIVE);
            if (inner && inner !== event.currentTarget && event.currentTarget.contains(inner)) return;
            onActivate(index);
        }
        : undefined;

    const state = React.useMemo<TypeListItemState>(
        () => ({ index, active, current, checked: Boolean(checked), layout }),
        [index, active, current, checked, layout],
    );

    return (
        <motion.div
            ref={measure ? (node: HTMLDivElement | null) => measure(measureKey ?? itemKey, node) : undefined}
            role="listitem"
            className="bd-list-cell"
            data-layout={layout}
            data-list-index={index}
            data-interactive={onActivate ? true : undefined}
            data-current={current || undefined}
            data-checked={checked || undefined}
            aria-current={current ? 'true' : undefined}
            aria-label={label}
            aria-posinset={posinset}
            aria-setsize={setsize}
            tabIndex={tabIndex}
            onClick={onClick}
            onKeyDown={(event) => onKeyDown(event, index)}
            onFocus={(event) => { if (event.target === event.currentTarget) onFocusKey(itemKey); }}
            variants={animated ? FADE_UP : undefined}
            exit={animated ? 'exit' : undefined}
            layout={animated ? 'position' : undefined}
        >
            {checked !== null && onToggle ? (
                <div className="bd-list-check" data-at={checkAt}>
                    <ListCheck checked={checked} label={checkLabel ?? ''} onToggle={(extend) => onToggle(index, extend)} />
                </div>
            ) : null}
            <div className="bd-list-content">{render(item, state)}</div>
        </motion.div>
    );
}

export default React.memo(ListCell) as typeof ListCell;
